import { useIntl } from 'react-intl';

import {
  CohortCharacteristicsResultType,
  CohortSummaryAttributeResult,
  CohortSummaryDetails,
} from '@verantos-dev/types-cohorts';

import { cohortCharacteristicResultTypeToTabularSectionNameMap } from '../constants';
import { sanitizeAgeAtIndexData, sanitizeGenderData } from '../helpers';
import type { CohortCharacteristicsTabularDataRow } from '../types';

export const useCohortCharacteristicsTabularData = (data: CohortSummaryDetails) => {
  const intl = useIntl();

  // Utility function to format percentages
  const formatPercentage = (count: number, percentage: number): string => {
    return `${count.toLocaleString()} (${percentage}%)`;
  };

  // Memoize rows to prevent re-creation on every render
  let idCounter = 0;
  const result: CohortCharacteristicsTabularDataRow[] = [];

  // Helper function to add category and entries to the rows
  const addCategoryAndEntries = (
    categoryTitle: string,
    entries?: CohortSummaryAttributeResult[]
  ) => {
    result.push({
      id: idCounter++,
      category: categoryTitle,
      name: '',
      value: '',
    });

    entries?.forEach((entry) => {
      result.push({
        id: idCounter++,
        category: undefined,
        name: entry.attributeName,
        value: formatPercentage(entry.attributeCount, entry.attributePercentage),
      });
    });
  };

  // Add data for Age
  if (data.ageAtIndex) {
    addCategoryAndEntries(
      intl.formatMessage({
        id: cohortCharacteristicResultTypeToTabularSectionNameMap[
          CohortCharacteristicsResultType.AGE_AT_INDEX
        ],
      }),
      sanitizeAgeAtIndexData(data.ageAtIndex)
    );
  }

  // Add data for Gender
  if (data.gender) {
    addCategoryAndEntries(
      intl.formatMessage({
        id: cohortCharacteristicResultTypeToTabularSectionNameMap[
          CohortCharacteristicsResultType.GENDER
        ],
      }),
      sanitizeGenderData(data.gender)
    );
  }

  // Add data for Race
  if (data.race) {
    addCategoryAndEntries(
      intl.formatMessage({
        id: cohortCharacteristicResultTypeToTabularSectionNameMap[
          CohortCharacteristicsResultType.RACE
        ],
      }),
      data.race
    );
  }

  // Add data for Ethnicity
  if (data.ethnicity) {
    addCategoryAndEntries(
      intl.formatMessage({
        id: cohortCharacteristicResultTypeToTabularSectionNameMap[
          CohortCharacteristicsResultType.ETHNICITY
        ],
      }),
      data.ethnicity
    );
  }

  return result;
};
